import { ChangeDetectionStrategy, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { FixMeLater, QRCodeComponent, QRCodeElementType } from 'angularx-qrcode';
import { NotificationsService } from 'src/app/core/notifications/notifications.service';

const QR_WIDTH_MIN = 300
const QR_WIDTH_MAX = 1000

@Component({
    selector: 'sam4-qr-code',
    imports: [QRCodeComponent],
    templateUrl: './qr-code.component.html',
    styleUrl: './qr-code.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class QrCodeComponent {
    @ViewChild('qrCode', { static: false, read: ElementRef }) qrcElement!: ElementRef;
    @Input() qrData: string = ''
    @Input() qrFilename: string = 'qr-code'
    @Input() qrImageSrc: string = 'assets/logo_white_bg.png'
    @Input() elementType: QRCodeElementType = 'canvas'

    private _width = 300

    /** Pixel width/height of the QR symbol (angularx-qrcode); clamped 300–1000, default 300. */
    @Input()
    set width(value: number | string) {
        const n = Number(value);
        if (!Number.isFinite(n)) {
            this._width = 300;
            return;
        }
        this._width = Math.min(QR_WIDTH_MAX, Math.max(QR_WIDTH_MIN, Math.round(n)));
    }

    get width(): number {
        return this._width;
    }

    constructor(private notifications: NotificationsService,) {
    }

    private convertBase64ToBlob(Base64Image: string) {
        // split into two parts
        const parts = Base64Image.split(";base64,")
        // hold the content type
        const imageType = parts[0].split(":")[1]
        // decode base64 string
        const decodedData = window.atob(parts[1])
        // create unit8array of size same as row data length
        const uInt8Array = new Uint8Array(decodedData.length)
        // insert all character code into uint8array
        for (let i = 0; i < decodedData.length; ++i) {
            uInt8Array[i] = decodedData.charCodeAt(i)
        }
        // return blob image after conversion
        return new Blob([uInt8Array], { type: imageType })
    }

    saveAsImage() {
        let parentElement = null;

        if (!this.qrcElement) {
            console.error('QR element not found.');
            return;
        }

        const nativeEl = this.qrcElement.nativeElement;

        if (this.elementType === "canvas") {
            const canvas = nativeEl.querySelector("canvas");
            if (canvas) {
            parentElement = canvas.toDataURL("image/png");
            }
        } else if (this.elementType === "img" || this.elementType === "url") {
            const img = nativeEl.querySelector("img");
            if (img) {
            parentElement = img.src;
            }
        } else {
            this.notifications.error("Set elementType to 'canvas', 'img' or 'url'.")
            return;
        }

        if (parentElement) {
            const blobData = this.convertBase64ToBlob(parentElement);
            const blob = new Blob([blobData], { type: "image/png" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = this.qrFilename || 'qr-code.png';
            link.click();
        }
    }

    async copyAsImageToClipboard() {
        const el = this.qrcElement?.nativeElement;
        const dataUrl = this.elementType === 'canvas'
            ? el?.querySelector('canvas')?.toDataURL('image/png')
            : el?.querySelector('img')?.src;

        if (!dataUrl) return alert('QR code not found.');
        try {
            const blob = await (await fetch(dataUrl)).blob();
            await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
            this.notifications.message('QR code copied to clipboard!')

        } catch {
            this.notifications.error('Copy failed. Use HTTPS or localhost.')
        }
    }

}
